import React, { ReactNode, useCallback, useEffect, useState, ErrorInfo, useMemo } from "react";
// components
import { CACHE_POSTFIX } from "@swiggy-private/aatm-nirbhar-build/dist/constants/htmlCaching";
import { ErrorPage } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/ErrorPage";
import { NewRelicErrorBoundary } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/NewRelic";
import { AnalyticsContext } from "@swiggy-private/analytics";
import { BaseTheme } from "@swiggy-private/dls-web-common/dist/components/themes/Base";
import { FontStyle } from "@swiggy-private/dls-web-theme/dist/components/Fonts";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import { GetSwiggyHeadersResponsePayload } from "@swiggy-private/native-bridge/dist/interfaces";
// hooks
import type { AppProps } from "next/app";
import NextPage from "next/app";
import { NEW_RELIC_APPLICATION_ID } from "../constants";
import { SwiggyHeadersProvider } from "../context/SwiggyHeadersContext";
import { useBootstrap } from "../hooks/useBootstrap";
// styles
import { GlobalStyle } from "../styles/globals";
import { ShuttleAppName } from "../styles/home";
import { getAnalyticsContextValue } from "../utils/analytics";
import { closeWebview } from "../utils/bridgeSdk";
import { getSwiggyHeaders } from "../utils/headers";
import { sendErrorBoundaryEvent } from "../utils/newrelic";

type NextPageWithLayout = NextPage & {
    getLayout?: (page: React.ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout;
};

const CLOSE = "click here to close";

function MyApp({ Component, pageProps, router }: AppPropsWithLayout) {
    // Detect if the page is a cached version
    const isCached = router.route.includes(CACHE_POSTFIX);
    const [headers, setHeaders] = useState<GetSwiggyHeadersResponsePayload>();

    useEffect(() => {
        let headersResponse;
        async function fetchData() {
            try {
                if (headers) {
                    return;
                }
                headersResponse = await getSwiggyHeaders();

                setHeaders(headersResponse);
            } catch (err) {}
        }
        fetchData();
    }, [headers]);

    // Use the layout defined at the page level, if available
    const getLayout = Component.getLayout ?? ((page) => page);

    const onError = useCallback(
        (error?: Error, errorInfo?: ErrorInfo) => {
            sendErrorBoundaryEvent(error, errorInfo, headers);
        },
        [headers],
    );

    const ErrorComponentWrapper = () => <ErrorPage onCtaClick={closeWebview} ctaText={CLOSE} />;

    useBootstrap("storefront_portal_gamification_frontend", isCached);

    const analyticsContextValue = useMemo(() => {
        return getAnalyticsContextValue(headers);
    }, [headers]);

    const headersContextValue = useMemo(() => ({ headers, setHeaders }), [headers]);

    return (
        <NewRelicErrorBoundary
            applicationID={NEW_RELIC_APPLICATION_ID}
            onErrorCapture={onError}
            ErrorComponent={ErrorComponentWrapper}
        >
            <ThemeProvider theme={BaseTheme} colorMode="light">
                <SwiggyHeadersProvider value={headersContextValue}>
                    <AnalyticsContext.Provider value={analyticsContextValue}>
                        <ShuttleAppName data-app-name={`${process.env.NEXT_PUBLIC_APP_NAME}`} />
                        <GlobalStyle />
                        <FontStyle />
                        {getLayout(<Component {...pageProps} />)}
                    </AnalyticsContext.Provider>
                </SwiggyHeadersProvider>
            </ThemeProvider>
        </NewRelicErrorBoundary>
    );
}

export default MyApp;
